import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { subscribeTenantModules, tenantModulesQueryKeys } from "@/lib/tenantModulesLive";


/**
 * Tenant module entitlements — Version 1 foundation (NOT enforced anywhere yet).
 *
 * This hook is intentionally unused by App, navigation, guards, workflows and
 * scanners. It exists so a future version can enforce entitlements without a
 * data-model migration. Resolution is deliberately FAIL-OPEN: a missing row for
 * a known module key resolves as enabled.
 */

export const TENANT_MODULE_KEYS = [
  "factory_portal",
  "outlet_portal",
  "logistics",
] as const;

export type TenantModuleKey = (typeof TENANT_MODULE_KEYS)[number];

export interface TenantModuleRow {
  module_key: string;
  enabled: boolean;
  configuration?: Record<string, unknown> | null;
}

export type TenantCapabilities = Record<TenantModuleKey, boolean>;

export function isTenantModuleKey(key: string): key is TenantModuleKey {
  return (TENANT_MODULE_KEYS as readonly string[]).includes(key);
}

/**
 * Pure resolver — unit testable, no React/Supabase dependency.
 * Missing rows fail open (true) for known module keys.
 */
export function resolveTenantCapabilities(
  rows: TenantModuleRow[] | null | undefined,
): TenantCapabilities {
  const byKey = new Map<string, TenantModuleRow>();
  for (const row of rows ?? []) {
    if (row && typeof row.module_key === "string") byKey.set(row.module_key, row);
  }

  return TENANT_MODULE_KEYS.reduce((acc, key) => {
    const row = byKey.get(key);
    acc[key] = row ? row.enabled === true : true; // fail open
    return acc;
  }, {} as TenantCapabilities);
}

/**
 * Pure membership check. Unknown module keys are always false.
 */
export function hasModule(
  rows: TenantModuleRow[] | null | undefined,
  moduleKey: string,
): boolean {
  if (!isTenantModuleKey(moduleKey)) return false;
  return resolveTenantCapabilities(rows)[moduleKey];
}

export function useTenantCapabilities(tenantIdOverride?: string | null) {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id ?? tenantIdOverride ?? undefined;

  const query = useQuery({
    queryKey: ["tenant-modules", tenantId],
    enabled: !!tenantId,
    // V7.1 — entitlements must respond to a Super Admin toggle inside a live
    // session. Short staleness + focus/reconnect refetch bound the propagation
    // delay without polling. Fail-open semantics are unchanged.
    staleTime: 30_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async (): Promise<TenantModuleRow[]> => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("tenant_modules")
        .select("module_key, enabled, configuration")
        .eq("tenant_id", tenantId);

      if (error) throw error;
      return (data ?? []) as TenantModuleRow[];
    },
  });

  // Live revocation: shared realtime channel + tab-visibility freshness check.
  useEffect(() => {
    if (!tenantId) return;

    const invalidate = () => {
      for (const key of tenantModulesQueryKeys(tenantId)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    };

    const unsubscribe = subscribeTenantModules(tenantId, invalidate);

    const onVisibility = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        invalidate();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      unsubscribe();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [tenantId, queryClient]);

  const capabilities = resolveTenantCapabilities(query.data);


  return {
    capabilities,
    rows: query.data,
    hasModule: (moduleKey: string) => hasModule(query.data, moduleKey),
    isLoading: !!tenantId && query.isLoading,
    error: query.error as Error | null,
  };
}
